/*
A MessagePack serializer.
See also: https://msgpack.org/
Based on: https://github.com/nailgilaziev/messagepack
Based on: https://github.com/ygoe/msgpack.js
*/

/*
Copyright 2020 Nail Gilaziev

Redistribution and use in source and binary forms, with or without modification,
are permitted provided that the following conditions are met:

1. Redistributions of source code must retain the above copyright notice,
   this list of conditions and the following disclaimer.

2. Redistributions in binary form must reproduce the above copyright notice,
  this list of conditions and the following disclaimer in the documentation and/or
  other materials provided with the distribution.

3. Neither the name of the copyright holder nor the names of its contributors may be used to
endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES,
INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

/*
Copyright © 2019, Yves Goergen, https://unclassified.software/source/msgpack-js

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and
associated documentation files (the “Software”), to deal in the Software without restriction,
including without limitation the rights to use, copy, modify, merge, publish, distribute,
sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or
substantial portions of the Software.

THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT
NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import { ArgumentException } from '../exceptions/argument-exception.js';
import { BinaryWriter } from './binary-writer.js';

export class BinaryPacker {
  private readonly writer: BinaryWriter;

  constructor(writer?: BinaryWriter) {
    this.writer = writer ?? new BinaryWriter();
  }

  packByte(byte: number): void {
    this.writer.writeByte(byte);
  }

  packNull(): void {
    this.writer.writeByte(0xc0);
  }

  packBool(value: boolean | null): void {
    if(value == null) {
      this.writer.writeByte(0xc0);
    }
    else {
      this.writer.writeByte(value ? 0xc3 : 0xc2);
    }
  }

  packInt(value: number | null): void {
    if(value == null) {
      this.writer.writeByte(0xc0);
      return;
    }

    if(value >= 0) {
      if(value <= 0xFF) {
        this.writer.writeByte(0xcc);
        this.writer.writeUint8(value);
      }
      else if(value <= 0xFFFF) {
        this.writer.writeByte(0xcd);
        this.writer.writeUInt16(value);
      }
      else if(value <= 0xFFFFFFFF) {
        this.writer.writeByte(0xce);
        this.writer.writeUInt32(value);
      }
      else {
        // this.writer.writeByte(0xcf);
        // this.writer.writeUint64(value);
        throw new ArgumentException('value', 'Uint64 is not supported by BinaryPacker');
      }
    }
    else if(value >= -128) {
      this.writer.writeByte(0xd0);
      this.writer.writeInt8(value);
    }
    else if(value >= -32768) {
      this.writer.writeByte(0xd1);
      this.writer.writeInt16(value);
    }
    else if(value >= -2147483648) {
      this.writer.writeByte(0xd2);
      this.writer.writeInt32(value);
    }
    else {
      // this.writer.writeByte(0xd3);
      // this.writer.writeInt64(value);
      throw new ArgumentException('value', 'Int64 is not supported by BinaryPacker');
    }
  }

  packDouble(value: number | null): void {
    if(value == null) {
      this.writer.writeByte(0xc0);
      return;
    }

    this.writer.writeByte(0xcb);
    this.writer.writeFloat64(value);
  }

  packString(value: string | null, options?: { emptyAsNull?: boolean }) {
    if(value == null || (value.length <= 0 && options?.emptyAsNull === true)) {
      this.writer.writeByte(0xc0);
      return;
    }

    const encoded = encodeUtf8(value);

    this.packLength(encoded.length);
    this.writer.writeBytes(encoded);
  }

  packBinaryBlob(buffer: Uint8Array | null): void {
    if(buffer == null) {
      this.writer.writeByte(0xc0);
      return;
    }

    this.packLength(buffer.length);
    this.writer.writeBytes(buffer);
  }

  packLength(value: number): void {
    if(value == null) {
      throw new ArgumentException('value', 'NULL is not a valid length');
    }
    else if(value < 0) {
      throw new ArgumentException('value', `${value} is not a valid length`);
    }
    else if(value <= 0xFF) {
      this.writer.writeByte(0xcc);
      this.writer.writeUint8(value);
    }
    else if(value <= 0xFFFF) {
      this.writer.writeByte(0xcd);
      this.writer.writeUInt16(value);
    }
    else if(value <= 0xFFFFFFFF) {
      this.writer.writeByte(0xce);
      this.writer.writeUInt32(value);
    }
    else {
      throw new ArgumentException('value', 'Max supported length is 0xFFFFFFFF');
    }
  }

  takeBytes(): Uint8Array {
    return this.writer.toBytes();
  }
}

// Encodes a string to UTF-8 bytes.
function encodeUtf8(str: string): Uint8Array {
  // Prevent excessive array allocation and slicing for all 7-bit characters
  const length = str.length;
  let ascii = true;
  for(let x = 0; x < length; x++) {
    if(str.charCodeAt(x) > 127) {
      ascii = false;
      break;
    }
  }

  // Based on: https://gist.github.com/pascaldekloe/62546103a1576803dade9269ccf76330
  const bytes = new Uint8Array(str.length * (ascii ? 1 : 4));
  let i = 0;
  for(let ci = 0; ci !== length; ci++) {
    let c = str.charCodeAt(ci);
    if(c < 128) {
      bytes[i++] = c;
      continue;
    }
    if(c < 2048) {
      bytes[i++] = c >> 6 | 192;
    }
    else {
      if(c > 0xd7ff && c < 0xdc00) {
        if(++ci >= length) {
          throw new Error('UTF-8 encode: incomplete surrogate pair');
        }

        const c2 = str.charCodeAt(ci);
        if(c2 < 0xdc00 || c2 > 0xdfff) {
          throw new Error('UTF-8 encode: second surrogate character 0x' + c2.toString(16) + ' at index ' + ci + ' out of range');
        }

        c = 0x10000 + ((c & 0x03ff) << 10) + (c2 & 0x03ff);
        bytes[i++] = c >> 18 | 240;
        bytes[i++] = c >> 12 & 63 | 128;
      }
      else {
        bytes[i++] = c >> 12 | 224;
      }
      bytes[i++] = c >> 6 & 63 | 128;
    }
    bytes[i++] = c & 63 | 128;
  }

  return ascii ? bytes : bytes.subarray(0, i);
}
